import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Input,
  Fab,
  Grid,
  Paper,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider
} from '@material-ui/core';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import EditIcon from '@material-ui/icons/Edit';
import ErrorIcon from '@material-ui/icons/Error';
import DateFnsUtils from '@date-io/date-fns';
import SelectReact from 'react-select';
import { UserType } from '../../validation';

import SaveCancelContainer from '../SaveCancelContainer';
import {
  UNDERAGE_VALUE,
  employeeAbilityTierOptions,
  employeeBookingTierOptions,
  productTierStyleMap
} from '../../constants/general';

import { calculateAge, formatDateOnlyString } from '../../helpers/helpers';

import {
  deleteContracts,
  fetchContracts,
  saveContracts,
} from '../../helpers/externalRequests';
import Dropdown from '../FormInput/Dropdown';
import ContractModalEditor from './ContractEditorModal';
import i18n from '../../i18n';

import '../../assets/stylesheets/EmployeeAttributes.css';

class PositionAttributes extends Component {
  constructor(props) {
    super(props);

    const { positions } = this.props;

    const selectedPositionIndex = 0;
    const selectedPositionData = positions[0] ? positions[0] : null;
    const lastUpdatedForPosition = positions[0]
      ? formatDateOnlyString(
        new Date(positions[0].StudioEmployee_Position.updatedAt)
      )
      : null;

    this.state = {
      contracts: [],
      inEditMode: false,
      positions,
      positionOptions: [],
      saving: false,
      selectedPositionIndex,
      selectedPositionData,
      lastUpdatedForPosition
    };

    this.initialPositionIndex = selectedPositionIndex;
    this.initialPositionData = selectedPositionData;

    this.manageContractsModalRef = React.createRef();
  }

  componentDidMount = () => {
    const { employee } = this.props;
    this.buildPositionOptions();
    if (employee.isContractor) {
      fetchContracts(employee.id).then(response => {
        this.setState({ contracts: response.data }, () => {
          const { contracts } = this.state;
          // the modal might not be rendered if the contract props are empty
          if (this.manageContractsModalRef.current) {
            this.manageContractsModalRef.current.formatContracts(
              contracts
            );
          }
        });
      });
    }
  };

  setContracts = incomingContracts => {
    const updatedContracts = [...incomingContracts];
    this.setState({ contracts: updatedContracts });
  };

  buildPositionOptions = () => {
    const { positions } = this.props;
    const positionOptions = [];

    positions.forEach((position, index) => {
      const positionOption = {
        id: index,
        label: i18n.t(`positions:${position.positionName}`),
        value: index,
        isActive: position.isActive,
      };

      positionOptions.push(positionOption);
    });

    this.setState({ positionOptions });
  };

  handleOnPositionChange = position => {
    const { positions } = this.state;

    this.setState({
      selectedPositionIndex: position.value,
      selectedPositionData: positions[position.value],
      lastUpdatedForPosition: formatDateOnlyString(
        new Date(positions[position.value].StudioEmployee_Position.updatedAt)
      )
    });

    this.initialPositionIndex = position.value;
    this.initialPositionData = positions[position.value];
  };

  handleAbilityAndPriorityTierChange = event => {
    const { selectedPositionData } = this.state;
    const positionData = JSON.parse(JSON.stringify(selectedPositionData));
    positionData.StudioEmployee_Position[event.target.name] = event.target.value;
    this.setState({ selectedPositionData: positionData });
  };

  handlePositionAttributeChange = (attribute, value) => {
    const { selectedPositionData } = this.state;
    const positionData = JSON.parse(JSON.stringify(selectedPositionData));
    positionData.StudioEmployee_Position.extras[attribute] = value;
    this.setState({ selectedPositionData: positionData });
  };

  handlePositionSubAttributeChange = (parentAttribute, subAttribute, value) => {
    const { selectedPositionData } = this.state;
    const positionData = JSON.parse(JSON.stringify(selectedPositionData));
    positionData.StudioEmployee_Position[parentAttribute][subAttribute] = value;
    this.setState({ selectedPositionData: positionData });
  };

  handleSaveContracts = async (contracts) => {
    const {
      displaySnackbar,
      employee: {
        id,
      },
    } = this.props;
    try {
      await saveContracts(contracts, id);
      displaySnackbar(i18n.t('userProfile:contractSaveSuccess'));
      const response = await fetchContracts(id);
      this.setContracts(response.data);
    } catch (error) {
      displaySnackbar(`${i18n.t('userProfile:contractSaveError')}: ${error}`);
    }
  };

  handleDeleteContracts = async (contracts) => {
    const {
      displaySnackbar,
      employee: {
        id,
      }
    } = this.props;
    try {
      await deleteContracts(contracts);
      displaySnackbar(i18n.t('userProfile:contractDeleteSuccess'));
      const response = await fetchContracts(id);
      this.setContracts(response.data);
    } catch (error) {
      displaySnackbar(`${i18n.t('userProfile:contractDeleteError')}: ${error}`);
    }
  };

  openContractModal = () => {
    this.manageContractsModalRef.current.showDialog();
  };

  toggleEditForm = () => {
    this.setState(prevstate => ({ inEditMode: !prevstate.inEditMode }));
  };

  executeSubmit = async () => {
    const { selectedPositionData } = this.state;
    const positionId = selectedPositionData.id;
    const { onSubmitPositionAttributeChange } = this.props;
    try {
      await onSubmitPositionAttributeChange(
        positionId,
        selectedPositionData.StudioEmployee_Position.abilityTier,
        selectedPositionData.StudioEmployee_Position.bookingPriority,
        selectedPositionData.StudioEmployee_Position.rate,
        selectedPositionData.StudioEmployee_Position.extras
      );
      this.setState({ inEditMode: false, saving: false });
      this.baseState = this.state;
    } catch {
      this.cancelForm();
    }
  }

  submitForm = () => {
    this.setState({ saving: true }, () => this.executeSubmit());
  };

  cancelForm = () => {
    this.setState({
      inEditMode: false,
      saving: false,
      selectedPositionData: this.initialPositionData
    });
  };

  displayEmployeeAbilityAndBookingTiering = (attribute) => {
    const { selectedPositionData, inEditMode } = this.state;
    const value = selectedPositionData.StudioEmployee_Position[attribute];
    const employeeTierOptions = (attribute === 'abilityTier') ? employeeAbilityTierOptions : employeeBookingTierOptions;
    return (
      <TableRow>
        <TableCell className="externalAttribute">
          {i18n.t(`booking:${attribute}`)}
        </TableCell>
        <TableCell className={`${productTierStyleMap[value]}`}>
          <Dropdown
            disabled={!inEditMode}
            className="studioDataRight studioDataDropdown largeAndThick"
            value={value}
            name={attribute}
            options={employeeTierOptions}
            onChange={event => this.handleAbilityAndPriorityTierChange(event)}
          />
        </TableCell>
      </TableRow>
    );
  };

  displayPositionRates = () => {
    const { selectedPositionData } = this.state;
    const rates = selectedPositionData.StudioEmployee_Position.rate;
    return (
      <TableRow>
        <TableCell className="attributeLabel">
          {i18n.t('booking:rate')}
        </TableCell>
        <TableCell>{this.generateSubTable('rate', rates)}</TableCell>
        {this.displayPositionTaxes()}
      </TableRow>
    );
  };

  displayPositionTaxes = () => {
    const { selectedPositionData, inEditMode } = this.state;
    const { extras } = selectedPositionData.StudioEmployee_Position;
    return Object.keys(extras)
      .filter(extra => extra === 'should charge taxes' || extra === 'taxes')
      .map((extra) => {
        return (
          <Fragment key={extra}>
            <TableCell className="attributeLabel">
              {i18n.t(`booking:${extra}`)}
            </TableCell>
            <TableCell>
              <FormGroup row>
                <FormControlLabel
                  disabled={!inEditMode}
                  control={
                    <Checkbox
                      checked={extras[extra]}
                      color="secondary"
                      onChange={() =>
                        this.handlePositionAttributeChange(
                          extra,
                          !extras[extra]
                        )
                      }
                    />
                  }
                />
              </FormGroup>
            </TableCell>
          </Fragment>
        );
      });
  };

  displayAttributes = () => {
    const { selectedPositionData, inEditMode } = this.state;

    const { extras } = selectedPositionData.StudioEmployee_Position;
    const { attributeControls } = selectedPositionData;

    return Object.keys(extras)
      .filter(extra => extra !== 'should charge taxes' && extra !== 'taxes')
      .map((extra) => {
        const typeOfExtra = this.getTypeOfExtra(attributeControls, extra);
        switch (typeOfExtra) {
          case 'boolean':
            return (
              <TableRow key={extra}>
                <TableCell className="attributeLabel">
                  {i18n.t(`booking:${extra}`)}
                </TableCell>
                <TableCell>
                  <FormGroup row>
                    <FormControlLabel
                      disabled={!inEditMode}
                      control={
                        <Checkbox
                          checked={extras[extra]}
                          color="secondary"
                          onChange={() =>
                            this.handlePositionAttributeChange(
                              extra,
                              !extras[extra]
                            )
                          }
                        />
                      }
                    />
                  </FormGroup>
                </TableCell>
              </TableRow>
            );
          case 'site':
          case 'link':
            return (
              <TableRow key={extra}>
                <TableCell className="attributeLabel">
                  {i18n.t(`booking:${extra}`)}
                </TableCell>
                <TableCell>
                  {!inEditMode ? (
                    <a href={extras[extra]}>{extras[extra]}</a>
                  ) : (
                    <Input
                      className="attributeValue"
                      name={extra}
                      value={extras[extra] != null ? extras[extra] : ''}
                      onChange={event =>
                        this.handlePositionAttributeChange(
                          extra,
                          event.target.value
                        )
                      }
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          case 'date': {
            const birthday = new Date(extras[extra]);
            const age = calculateAge(birthday);

            return (
              <TableRow key={extra}>
                <TableCell className="attributeLabel">
                  {i18n.t(`booking:${extra}`)}
                </TableCell>
                <TableCell>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                      disabled={!inEditMode}
                      format="MM/dd/yyyy"
                      value={birthday}
                      onChange={date =>
                        this.handlePositionAttributeChange(
                          'birthday',
                          date.getTime()
                        )
                      }
                    />
                    <span>
                      {`(${i18n.t('age')} ${age})`}
                      {age < UNDERAGE_VALUE && (
                        <ErrorIcon className="underage" />
                      )}
                    </span>
                  </MuiPickersUtilsProvider>
                </TableCell>
              </TableRow>
            );
          }

          default:
            return (
              <TableRow key={extra}>
                <TableCell className="attributeLabel">
                  {i18n.t(`booking:${extra}`)}
                </TableCell>
                <TableCell>
                  <Input
                    className="attributeValue"
                    name={extra}
                    value={extras[extra] != null ? extras[extra] : ''}
                    onChange={event =>
                      this.handlePositionAttributeChange(
                        extra,
                        event.target.value
                      )
                    }
                    disabled={!inEditMode}
                    disableUnderline={!inEditMode}
                  />
                </TableCell>
              </TableRow>
            );
        }
      });
  };

  generateSubTable = (parentAttribute, subTableData) => {
    const { inEditMode } = this.state;
    return (
      <Table>
        <TableBody>
          {Object.keys(subTableData).map(attribute => (
            <TableRow key={attribute}>
              <TableCell className="attributeLabel">
                {i18n.t(`booking:${attribute}`)}
              </TableCell>
              <TableCell>
                <Input
                  name={attribute}
                  value={
                    subTableData[attribute] != null
                      ? subTableData[attribute]
                      : ''
                  }
                  onChange={event =>
                    this.handlePositionSubAttributeChange(
                      parentAttribute,
                      attribute,
                      event.target.value
                    )
                  }
                  disabled={!inEditMode}
                  disableUnderline={!inEditMode}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  getContractsByPosition = id => {
    const { contracts } = this.state;
    return contracts.filter(contract => contract.positionId === id);
  };

  getTypeOfExtra = (attributeControls, extraName) => {
    for (let i = 0; i < attributeControls.length; i++) {
      const attribute = attributeControls[i];
      if (attribute.attributeName === extraName) {
        return attribute.valueType;
      }
    }
    return null;
  };

  render() {
    const {
      contracts,
      inEditMode,
      positions,
      positionOptions,
      saving,
      selectedPositionIndex,
      selectedPositionData,
      lastUpdatedForPosition
    } = this.state;

    const {
      employee,
      user
    } = this.props;

    const positionContracts =
      employee.isContractor && contracts.length
        ? this.getContractsByPosition(selectedPositionData.id)
        : [];

    if (!positions.length) {
      return (
        <>
          <Paper className="employeeAttributesContainer">
            {i18n.t('userProfile:assignPosition')}
          </Paper>
        </>
      );
    }

    const canEdit = user.scopes.includes('employees:write') && positions[selectedPositionIndex].isActive;
    const canSeeRates = user.scopes.includes('employee-rates:read')
      || (employee.email === user.email && user.scopes.includes('my-rates:read'))

    return (
      <>
        <Paper className="employeeAttributesContainer">
          <Grid container>
            <Grid item xs={5}>
              <SelectReact
                isDisabled={inEditMode}
                className="positionSelectDropdown"
                hideSelectedOptions
                value={positionOptions[selectedPositionIndex]}
                menuShouldScrollIntoView={false}
                options={positionOptions}
                onChange={this.handleOnPositionChange}
              />
            </Grid>
            {canEdit && !inEditMode && (
              <Grid item xs={1}>
                <Fab
                  className="editButton"
                  variant="extended"
                  size="small"
                  color="primary"
                  aria-label="edit"
                  onClick={this.toggleEditForm}
                >
                  <EditIcon color="secondary" />
                </Fab>
              </Grid>
            )}
            {employee.isContractor && (
              <Grid item xs={5}>
                <Button
                  className="ssenseButton ssenseSecondary"
                  onClick={this.openContractModal}
                >
                  {i18n.t('userProfile:manageContracts')}
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <Table>
                <TableBody>{this.displayEmployeeAbilityAndBookingTiering('abilityTier')}</TableBody>
              </Table>
              <Table>
                <TableBody>{this.displayEmployeeAbilityAndBookingTiering('bookingPriority')}</TableBody>
              </Table>

              {canSeeRates && (
                <Table>
                  <TableBody>{this.displayPositionRates()}</TableBody>
                </Table>
              )}
              <Divider />
              <Table>
                <TableBody>{this.displayAttributes()}</TableBody>
              </Table>

              {inEditMode && (
                <SaveCancelContainer
                  onSave={this.submitForm}
                  onCancel={this.cancelForm}
                  saving={saving}
                />
              )}
            </Grid>
          </Grid>
          <ContractModalEditor
            ref={this.manageContractsModalRef}
            contracts={positionContracts}
            positionLabel={
              positionOptions[selectedPositionIndex]
                ? positionOptions[selectedPositionIndex].label
                : ''
            }
            positionId={selectedPositionData.id}
            handleSaveContracts={this.handleSaveContracts}
            handleDeleteContracts={this.handleDeleteContracts}
          />
        </Paper>
        <p>
          {`${i18n.t('userProfile:lastUpdated')} ${lastUpdatedForPosition}`}
        </p>
      </>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
});

PositionAttributes.propTypes = {
  displaySnackbar: PropTypes.func.isRequired,
  positions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSubmitPositionAttributeChange: PropTypes.func.isRequired,
  employee: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object
    ])
  ).isRequired,
  user: UserType.isRequired,
};

export { PositionAttributes as PositionAttributesNoConnect };
export default connect(mapStateToProps)(PositionAttributes)
